import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { providerPartners, providerBranches, providerAddBranch } from "../../api/accountsApi";
import "./index.css";

const errText = (err, fallback) =>
  err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;

/**
 * Partner + branch of a broadband provider login (Internet or DVPL), for the Add / Edit
 * admin & operator forms. Once the base URL and token are filled in, the partners are
 * fetched; picking a partner fetches its branches. "Add branch" creates a branch for the
 * picked partner named after the account's username, with the account's address.
 *
 * Props:
 *   type          "internet" | "dvpl"
 *   baseUrl, token  the provider login typed in the form
 *   accountId     existing account (edit) — lets the server use the saved token when the
 *                 form only shows it masked
 *   partnerId, branchId   current values
 *   onChange      ({ partnerId, branchId }) => void
 *   branchName    the account's username (name of a new branch)
 *   address       the account's address (address of a new branch)
 */
const ProviderBranchPicker = ({ type, baseUrl, token, accountId, partnerId, branchId, onChange, branchName, address }) => {
  const [partners, setPartners] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loadingP, setLoadingP] = useState(false);
  const [loadingB, setLoadingB] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  const [confirmAdd, setConfirmAdd] = useState(false);
  const seq = useRef(0);

  const creds = useCallback(
    () => ({ type, base_url: baseUrl || "", token: token || "", account_id: accountId || undefined }),
    [type, baseUrl, token, accountId]
  );
  const ready = !!(baseUrl && (token || accountId));

  const loadPartners = useCallback(async () => {
    if (!ready) return;
    const my = ++seq.current;
    setLoadingP(true);
    setError("");
    try {
      const { data } = await providerPartners(creds());
      if (my !== seq.current) return;
      setPartners(data.partners || []);
    } catch (err) {
      if (my !== seq.current) return;
      setPartners([]);
      setError(errText(err, "Could not load partners"));
    } finally {
      if (my === seq.current) setLoadingP(false);
    }
  }, [ready, creds]);

  const loadBranches = useCallback(async (pid, selectId) => {
    if (!pid || !ready) {
      setBranches([]);
      return;
    }
    setLoadingB(true);
    try {
      const { data } = await providerBranches({ ...creds(), partner_id: Number(pid) });
      setBranches(data.branches || []);
      if (selectId) onChange({ partnerId: String(pid), branchId: String(selectId) });
    } catch (err) {
      setBranches([]);
      setError(errText(err, "Could not load branches"));
    } finally {
      setLoadingB(false);
    }
  }, [ready, creds, onChange]);

  // Fetch partners once the URL and token are filled in (debounced while typing)
  useEffect(() => {
    if (!ready) {
      setPartners([]);
      setBranches([]);
      return undefined;
    }
    const t = setTimeout(loadPartners, 600);
    return () => clearTimeout(t);
  }, [ready, baseUrl, token, loadPartners]);

  // Branches of the saved / chosen partner
  useEffect(() => {
    if (partnerId && ready) loadBranches(partnerId);
    else setBranches([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId, ready, baseUrl, token]);

  const addBranch = async () => {
    setAdding(true);
    try {
      const { data } = await providerAddBranch({
        ...creds(),
        partner_id: Number(partnerId),
        branch_name: branchName,
        address,
      });
      toast.success(data.message || "Branch added");
      setConfirmAdd(false);
      await loadBranches(partnerId, data.branch?.id || null);
    } catch (err) {
      toast.error(errText(err, "Could not add the branch"));
    } finally {
      setAdding(false);
    }
  };

  const hasPartner = partners.some((p) => String(p.id) === String(partnerId));
  const hasBranch = branches.some((b) => String(b.id) === String(branchId));
  const canAdd = !!partnerId && !!(branchName || "").trim() && !!(address || "").trim();

  return (
    <div className="pbp">
      <div className="pbp-row">
        <label className="pbp-field">
          <span>Partner</span>
          <select
            value={partnerId || ""}
            disabled={!ready || loadingP}
            onChange={(e) => onChange({ partnerId: e.target.value, branchId: "" })}
          >
            <option value="">{!ready ? "Enter base URL and token" : loadingP ? "Loading partners…" : "Select partner"}</option>
            {partnerId && !hasPartner && <option value={partnerId}>Partner #{partnerId} (saved)</option>}
            {partners.map((p) => (
              <option key={p.id} value={p.id}>{p.name} (#{p.id})</option>
            ))}
          </select>
        </label>
        <button type="button" className="pbp-refresh" onClick={loadPartners} disabled={!ready || loadingP} title="Reload partners">
          ↻
        </button>
      </div>

      <div className="pbp-row">
        <label className="pbp-field">
          <span>Branch</span>
          <select
            value={branchId || ""}
            disabled={!partnerId || loadingB}
            onChange={(e) => onChange({ partnerId, branchId: e.target.value })}
          >
            <option value="">{!partnerId ? "Select a partner first" : loadingB ? "Loading branches…" : "Select branch"}</option>
            {branchId && !hasBranch && <option value={branchId}>Branch #{branchId} (saved)</option>}
            {branches.map((b) => (
              <option key={b.id} value={b.id}>{b.name} (#{b.id})</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="pbp-add"
          disabled={!canAdd || adding}
          title={canAdd ? "Add a branch for this partner" : "Pick a partner and fill in the username and address first"}
          onClick={() => setConfirmAdd(true)}
        >
          + Add branch
        </button>
      </div>

      {confirmAdd && (
        <div className="pbp-confirm">
          <div>
            Add branch <strong>{branchName}</strong> to partner #{partnerId}
            <div className="pbp-hint">Address: {address}</div>
          </div>
          <div className="pbp-confirm__btns">
            <button type="button" onClick={() => setConfirmAdd(false)} disabled={adding}>Cancel</button>
            <button type="button" className="is-primary" onClick={addBranch} disabled={adding}>
              {adding ? "Adding…" : "Add branch"}
            </button>
          </div>
        </div>
      )}

      {error && <div className="pbp-error">{error}</div>}
    </div>
  );
};

export default ProviderBranchPicker;
